import type { Disposable } from "@/shared/contracts/lifecycle";
import {
  createUpgradeHoverRepeater,
  type UpgradeHoverRepeater,
  type UpgradeRepeatEventBudgetSource,
} from "@/features/hoverIdle/hover/upgradeHoverRepeater";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import {
  HOV_AMPLIFIER_UPGRADE_REPEAT_DECAY,
  HOV_AMPLIFIER_UPGRADE_REPEAT_MIN_INTERVAL_MS,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import type { HoverIdleSnapshot } from "@/features/hoverIdle/types";

// Runtime-only automation that treats each Amplifier upgrade MP assignment as a
// continuously held hover over the matching upgrade button. It reuses the same
// hover-repeat timer, min interval, and decay as the manual Amplifier upgrade
// interaction so MP-driven and manual purchasing share one cadence. The repeat
// timer state lives only here; restore/reset rebuild it by re-syncing, which
// restarts each target from its repeat-start interval.
export type MpmAmplifierUpgradeAutomationSnapshot = Pick<
  HoverIdleSnapshot,
  "mpm" | "hovAmplifier"
>;

export interface MpmAmplifierUpgradeAutomationControllerOptions {
  // Authoritative per-pulse purchase. Returns whether a purchase happened so a
  // failed pulse resets the cadence without advancing count or cost.
  readonly onPurchaseAmplifierUpgrade: (
    entryId: HovAmplifierEntryId,
  ) => boolean;
  // Coalesced presentation/persistence side effect, fired once per repeater
  // burst when at least one purchase succeeded. Keeps a large catch-up frame
  // from running one full refresh + autosave per pulse.
  readonly onPurchasesApplied?: () => void;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly eventBudgetPerTick?: UpgradeRepeatEventBudgetSource;
}

export interface MpmAmplifierUpgradeAutomationController extends Disposable {
  sync(snapshot: MpmAmplifierUpgradeAutomationSnapshot): void;
}

interface ActiveAmplifierUpgradeAutomation {
  readonly entryId: HovAmplifierEntryId;
  readonly repeater: UpgradeHoverRepeater;
}

export function createMpmAmplifierUpgradeAutomationController(
  options: MpmAmplifierUpgradeAutomationControllerOptions,
): MpmAmplifierUpgradeAutomationController {
  let latestSnapshot: MpmAmplifierUpgradeAutomationSnapshot | null = null;
  const activeAutomations = new Map<
    HovAmplifierEntryId,
    ActiveAmplifierUpgradeAutomation
  >();

  let burstDepth = 0;
  let burstHadPurchase = false;

  const handleBurstStart = (): void => {
    if (burstDepth === 0) {
      burstHadPurchase = false;
    }
    burstDepth += 1;
  };

  const handleBurstEnd = (): void => {
    if (burstDepth <= 0) {
      return;
    }
    burstDepth -= 1;
    if (burstDepth === 0 && burstHadPurchase) {
      burstHadPurchase = false;
      options.onPurchasesApplied?.();
    }
  };

  const purchaseTarget = (entryId: HovAmplifierEntryId): boolean => {
    if (!isAmplifierUpgradeMpTargetPurchasable(entryId, latestSnapshot)) {
      return false;
    }
    const purchased = options.onPurchaseAmplifierUpgrade(entryId);
    if (purchased) {
      burstHadPurchase = true;
    }
    return purchased;
  };

  const stopAutomation = (entryId: HovAmplifierEntryId): void => {
    const active = activeAutomations.get(entryId);
    if (active === undefined) {
      return;
    }
    active.repeater.dispose();
    activeAutomations.delete(entryId);
  };

  const startAutomation = (entryId: HovAmplifierEntryId): void => {
    const repeater = createUpgradeHoverRepeater({
      initialIntervalMs: options.initialIntervalMs,
      minIntervalMs: HOV_AMPLIFIER_UPGRADE_REPEAT_MIN_INTERVAL_MS,
      decay: HOV_AMPLIFIER_UPGRADE_REPEAT_DECAY,
      speedMultiplier: options.speedMultiplier,
      eventBudgetPerTick: options.eventBudgetPerTick,
      onBurstStart: handleBurstStart,
      onBurstEnd: handleBurstEnd,
      onFire: () => purchaseTarget(entryId),
    });
    activeAutomations.set(entryId, { entryId, repeater });
    repeater.setEngaged(true);
  };

  return {
    sync(snapshot: MpmAmplifierUpgradeAutomationSnapshot): void {
      latestSnapshot = snapshot;
      const nextTargets = computeActiveAmplifierUpgradeMpTargets(snapshot);

      for (const entryId of [...activeAutomations.keys()]) {
        if (!nextTargets.has(entryId)) {
          stopAutomation(entryId);
        }
      }

      for (const entryId of nextTargets) {
        if (!activeAutomations.has(entryId)) {
          startAutomation(entryId);
        }
      }
    },
    dispose(): void {
      latestSnapshot = null;
      for (const entryId of [...activeAutomations.keys()]) {
        stopAutomation(entryId);
      }
    },
  };
}

// Active = section unlocked (gated by the amplifier-upgrade achievement) and
// enabled, intersected with assigned entries whose upgrade action is available
// (the amplifier entry itself is unlocked). Affordability fluctuates per pulse
// and is checked at fire time instead, so a target stays a held hover while it
// waits for HovCore.
export function computeActiveAmplifierUpgradeMpTargets(
  snapshot: MpmAmplifierUpgradeAutomationSnapshot,
): ReadonlySet<HovAmplifierEntryId> {
  const section = snapshot.mpm.amplifierUpgrades;
  if (!section.unlocked || !section.enabled) {
    return new Set();
  }
  const availableEntryIds = new Set<HovAmplifierEntryId>(
    snapshot.hovAmplifier.entries
      .filter((entry) => entry.unlocked && entry.mpAssignable)
      .map((entry) => entry.id),
  );
  const targets = new Set<HovAmplifierEntryId>();
  for (const entryId of section.assignments) {
    if (availableEntryIds.has(entryId)) {
      targets.add(entryId);
    }
  }
  return targets;
}

export function isAmplifierUpgradeMpTargetPurchasable(
  entryId: HovAmplifierEntryId,
  snapshot: MpmAmplifierUpgradeAutomationSnapshot | null,
): boolean {
  if (snapshot === null) {
    return false;
  }
  const entry = snapshot.hovAmplifier.entries.find(
    (candidate) => candidate.id === entryId,
  );
  return entry?.canPurchaseUpgrade === true;
}
